import React, { useRef, useContext, useState, useCallback, useEffect } from 'react';
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBTypography,
  MDBBtn,
  MDBAlert,
  MDBCard,
  MDBCardBody,
  MDBIcon,
  MDBBadge,
  MDBSpinner
} from 'mdb-react-ui-kit';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { useFormData } from '../hooks/useFormData';
import { useStepNavigation } from '../hooks/useStepNavigation';
import { useValidation } from '../components/ValidationProvider';
import { useFileUpload } from '../hooks/useFileUpload';
import { DevContext } from '../../../../../App';

export default function ImageStep({ onComplete }) {
  const { t } = useTranslation();
  const { formData, handleFieldChange, hasFieldError, getFieldError } = useFormData();
  const { goToNextStep, goToPreviousStep } = useStepNavigation();
  const { getStepValidation, getStepRequiredFields, getFieldValidation } = useValidation();
  const { isDevMode } = useContext(DevContext);
  const fileInputRef = useRef(null);
  
  // Use the file upload hook
  const { 
    uploadLectureImage, 
    isUploading, 
    uploadError: hookUploadError,
    resetUploadState 
  } = useFileUpload();
  
  const [imagePreview, setImagePreview] = useState(null);
  const [localUploadError, setLocalUploadError] = useState(null);

  // Get validation info from schema
  const stepValidation = getStepValidation('image');
  const requiredFields = getStepRequiredFields('image');
  const imageFieldValidation = getFieldValidation('image');

  // Set initial preview if image already exists
  useEffect(() => {
    if (formData.image?.fileName && !imagePreview) {
      setImagePreview(`https://iec-cp-public.s3.eu-central-1.amazonaws.com/lectureImages/${formData.image.fileName}`);
    }
  }, [formData.image, imagePreview]);

  const processFile = async (file) => {
    try {
      setLocalUploadError(null);
      
      // Upload using the hook
      const uploadedImage = await uploadLectureImage(file);
      
      if (uploadedImage) {
        // Update form data with uploaded image info
        handleFieldChange('image', uploadedImage);
        
        // Set preview URL
        setImagePreview(URL.createObjectURL(file));
      }
    } catch (error) {
      console.error('Upload error:', error);
      setLocalUploadError(error.message || t('Upload failed. Please try again.'));
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleRemoveImage = () => {
    handleFieldChange('image', null);
    setImagePreview(null);
    setLocalUploadError(null);
    resetUploadState();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      processFile(acceptedFiles[0]);
    }
  }, []);
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ 
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif']
    },
    multiple: false
  });

  const handleContinue = () => {
    // Image might be required based on schema, check validation
    if (stepValidation.isValid) {
      onComplete?.();
      goToNextStep();
    }
  };

  const handleSkip = () => {
    // Only allow skip if image is not required
    if (!requiredFields.includes('image') || stepValidation.isValid) {
      handleFieldChange('image', null);
      onComplete?.();
      goToNextStep();
    }
  };

  const canContinue = stepValidation.isValid;
  const canSkip = !requiredFields.includes('image') || stepValidation.isValid;

  return (
    <div className="d-flex flex-column h-100">
      <MDBContainer className="flex-grow-1 pb-5">
      <MDBTypography tag="h3" className="mb-4">
        {t('Lecture Image')}
      </MDBTypography>
      
      <MDBRow className="mb-4">
        <MDBCol>
          <MDBTypography tag="p" className="mb-4 text-muted">
            {
t(`Add an image to make your presentation more engaging so the topic immediately catches the reader's eye.
Please always choose the 16:9 format. The system would automatically crop other formats and potentially miss important content.`)}
          </MDBTypography>
          
          {!formData.image && !imagePreview ? (
            <div {...getRootProps()}>
              <input {...getInputProps()} />
              <MDBCard 
                className={`text-center p-4 cursor-pointer ${
                  isDragActive ? 'border-primary' : 'border-dashed'
                }`}
                style={{ 
                  cursor: 'pointer',
                  borderStyle: 'dashed',
                  borderWidth: '2px',
                  borderColor: isDragActive ? '#0d6efd' : '#dee2e6',
                  backgroundColor: isDragActive ? '#f8f9fa' : 'transparent'
                }}
              >
                <MDBCardBody>
                  {isUploading ? (
                    <>
                      <MDBSpinner role="status" className="mb-3" />
                      <MDBTypography tag="h6" className="mb-2">
                        {t('Uploading image...')}
                      </MDBTypography>
                    </>
                  ) : (
                    <>
                      <MDBIcon 
                        icon="cloud-upload-alt" 
                        size="3x" 
                        className="text-muted mb-3"
                      />
                      <MDBTypography tag="h6" className="mb-2">
                        {isDragActive ? t('Drop image here') : t('Upload Lecture Image')}
                      </MDBTypography>
                      <MDBTypography tag="p" className="text-muted small mb-3">
                        {t('Drag and drop an image file or click to select')}
                      </MDBTypography>
                      <MDBTypography tag="small" className="text-muted">
                        {t('Supported formats: JPG, PNG (max 5MB)')}<br/>
                        <strong>{t('Recommended format: 16:9')}</strong>
                      </MDBTypography>
                    </>
                  )}
                </MDBCardBody>
              </MDBCard>
            </div>
          ) : (
            <MDBCard>
              <MDBCardBody>
                {/* 16:9 Preview */}
                <div className="mb-4 text-center">
                  <img
                    src={imagePreview || (formData.image?.fileName ? 
                      `https://iec-cp-public.s3.eu-central-1.amazonaws.com/lectureImages/${formData.image.fileName}` : 
                      'https://via.placeholder.com/600x338?text=No+Image')}
                    className="img-thumbnail mx-auto d-block"
                    style={{ 
                      aspectRatio: '16/9', 
                      objectFit: 'cover',
                      width: '100%',
                      maxWidth: '500px',
                      height: 'auto',
                      margin: '1rem 0'
                    }}
                    alt="Lecture preview in 16:9 format" 
                  />
                  <MDBTypography tag="small" className="text-muted d-block mt-3">
                    {t('Preview in 16:9 format - this is how your image will appear to users')}
                  </MDBTypography>
                </div>
                
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <MDBIcon 
                      icon="image" 
                      className="text-success me-3" 
                      size="2x"
                    />
                    <div>
                      <MDBTypography tag="h6" className="mb-1">
                        {formData.image?.file?.name || formData.image?.fileName || 'Uploaded Image'}
                      </MDBTypography>
                      {formData.image?.file?.size && (
                        <MDBTypography tag="small" className="text-muted">
                          {(formData.image.file.size / 1024 / 1024).toFixed(2)} MB
                        </MDBTypography>
                      )}
                    </div>
                  </div>
                  <MDBBtn
                    color="danger"
                    size="sm"
                    onClick={handleRemoveImage}
                    disabled={isUploading}
                  >
                    <MDBIcon icon="trash" />
                  </MDBBtn>
                </div>
              </MDBCardBody>
            </MDBCard>
          )}
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </MDBCol>
      </MDBRow>

      {(hasFieldError('image') || localUploadError || hookUploadError) && (
        <MDBRow className="mb-3">
          <MDBCol>
            <MDBAlert color="danger">
              {localUploadError || hookUploadError || getFieldError('image')}
            </MDBAlert>
          </MDBCol>
        </MDBRow>
      )}

      {/* Validation Debug Info */}
      {isDevMode && (
        <MDBRow className="mb-4">
          <MDBCol>
            <MDBCard className="border-info">
              <MDBCardBody>
                <MDBTypography tag="h6" className="mb-3">
                  🔍 Validation Debug Info (Dev Mode) - Image Step
                </MDBTypography>
              
              <div className="mb-2">
                <strong>Step Required Fields:</strong> {requiredFields.join(', ') || 'none'}
              </div>
              
              <div className="mb-2">
                <strong>Is Image Required:</strong>{' '}
                <MDBBadge color={requiredFields.includes('image') ? 'warning' : 'info'}>
                  {requiredFields.includes('image') ? 'Required by Schema' : 'Optional'}
                </MDBBadge>
              </div>
              
              <div className="mb-2">
                <strong>Current Image:</strong>{' '}
                {formData.image ? (
                  <>File: {formData.image.file?.name || formData.image.fileName || 'Uploaded Image'} 
                  {formData.image.file?.size && `(${(formData.image.file.size / 1024 / 1024).toFixed(2)} MB)`}</>
                ) : (
                  'No image selected'
                )}
              </div>
              
              <div className="mb-2">
                <strong>Upload Status:</strong>{' '}
                <MDBBadge color={isUploading ? 'info' : (localUploadError || hookUploadError) ? 'danger' : 'success'}>
                  {isUploading ? 'Uploading...' : (localUploadError || hookUploadError) ? 'Error' : 'Ready'}
                </MDBBadge>
              </div>
              
              <div className="mb-2">
                <strong>Schema Validation:</strong>{' '}
                <MDBBadge color={stepValidation.isValid ? 'success' : 'danger'}>
                  {stepValidation.isValid ? 'Valid' : 'Invalid'}
                </MDBBadge>
              </div>
              
              {stepValidation.errors.length > 0 && (
                <div className="mb-2">
                  <strong>Schema Errors:</strong>
                  <ul className="mb-0">
                    {stepValidation.errors.map((error, index) => (
                      <li key={index}>
                        <code>{error.field}</code>: {error.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {imageFieldValidation && (
                <div className="mb-2">
                  <strong>Field-Specific Error:</strong>{' '}
                  <code>{imageFieldValidation.field}</code>: {imageFieldValidation.message}
                </div>
              )}
              
              <div className="mb-2">
                <strong>Continue Button:</strong>{' '}
                <MDBBadge color={canContinue ? 'success' : 'warning'}>
                  {canContinue ? 'Enabled' : 'Disabled'}
                </MDBBadge>
              </div>
            </MDBCardBody>
          </MDBCard>
        </MDBCol>
      </MDBRow>
      )}

      </MDBContainer>
      
      {/* Fixed button container at bottom */}
      <div className="bg-white" style={{ 
        position: 'sticky', 
        bottom: 0,
        padding: '1rem',
        marginTop: 'auto',
        zIndex: 10
      }}>
        <MDBContainer>
          <MDBRow>
            <MDBCol className="d-flex justify-content-center gap-5">
              <MDBBtn 
                color="secondary" 
                onClick={goToPreviousStep}
                outline
              >
                {t('Back')}
              </MDBBtn>
              <MDBBtn 
                onClick={handleContinue}
                disabled={!canContinue}
              >
                {t('Continue')}
              </MDBBtn>
            </MDBCol>
          </MDBRow>
        </MDBContainer>
      </div>
    </div>
  );
}